import { useEffect, useRef } from 'react'

export type LegalConsents = {
  /** Consent to personal-data processing — a stand-alone document under 152-ФЗ since 01.09.2025. */
  pdn: boolean
  /** Acceptance of the user agreement. */
  terms: boolean
}

export const NO_CONSENTS: LegalConsents = { pdn: false, terms: false }

export function allConsentsGiven(value: LegalConsents): boolean {
  return value.pdn && value.terms
}

/**
 * One checkbox per document, none pre-checked, plus "Принять всё" on top as a shortcut.
 *
 * Each document gets its own explicit action because the personal-data consent must not be folded
 * into accepting something else; the shortcut is only a convenience over those same boxes — the
 * backend still records every document as its own row in the proof log. Shared by the registration
 * form and the consent gate so the two can never drift apart.
 */
export default function LegalConsentChecklist({
  value,
  onChange,
}: {
  value: LegalConsents
  onChange: (next: LegalConsents) => void
}) {
  const allRef = useRef<HTMLInputElement>(null)
  const all = allConsentsGiven(value)

  // Half-checked while only one of the two is ticked — the usual "select all" convention.
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = value.pdn !== value.terms
  }, [value.pdn, value.terms])

  return (
    <>
      <style>{`
        .lcc { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
        .lcc-row {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 11px 13px; border: 1.5px solid #e2e8f0; border-radius: 12px;
          background: #f8fafc; cursor: pointer; user-select: none;
          transition: border-color 0.15s, background 0.15s;
        }
        .lcc-row:hover { border-color: #86efac; }
        .lcc-row.checked { border-color: #15803d; background: #f0fdf4; }
        .lcc-row input {
          margin-top: 2px; width: 16px; height: 16px; min-width: 16px;
          accent-color: #15803d; cursor: pointer;
        }
        .lcc-row--all { background: #fff; }
        .lcc-all-text { font-size: 13px; font-weight: 700; color: #0f172a; line-height: 1.5; }
        .lcc-text { font-size: 12.5px; color: #475569; line-height: 1.55; }
        .lcc-text a { color: #15803d; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
      `}</style>
      <div className="lcc">
        <label className={`lcc-row lcc-row--all${all ? ' checked' : ''}`}>
          <input
            ref={allRef}
            type="checkbox"
            checked={all}
            onChange={(e) => onChange({ pdn: e.target.checked, terms: e.target.checked })}
          />
          <span className="lcc-all-text">Принять всё</span>
        </label>

        <label className={`lcc-row${value.pdn ? ' checked' : ''}`}>
          <input
            type="checkbox"
            checked={value.pdn}
            onChange={(e) => onChange({ ...value, pdn: e.target.checked })}
          />
          <span className="lcc-text">
            Даю{' '}
            <a href="/legal/consent" target="_blank" rel="noopener noreferrer">
              согласие на обработку персональных данных
            </a>{' '}
            на условиях{' '}
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">
              Политики обработки персональных данных
            </a>
          </span>
        </label>

        <label className={`lcc-row${value.terms ? ' checked' : ''}`}>
          <input
            type="checkbox"
            checked={value.terms}
            onChange={(e) => onChange({ ...value, terms: e.target.checked })}
          />
          <span className="lcc-text">
            Принимаю{' '}
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer">
              Пользовательское соглашение
            </a>
          </span>
        </label>
      </div>
    </>
  )
}
